import React from 'react';
import { Input } from '../../atoms/Input';
import { ErrorMessage } from '../../atoms/ErrorMessage';
import styles from './FormField.module.css';

export interface FormFieldProps {
  label: string;
  name: string;
  type?:
    | 'text'
    | 'email'
    | 'number'
    | 'password'
    | 'url'
    | 'textarea'
    | 'select';
  value: string | number;
  onChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => void;
  onBlur?: (
    e: React.FocusEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => void;
  error?: string;
  required?: boolean;
  placeholder?: string;
  helpText?: string;
  disabled?: boolean;
  maxLength?: number;
  min?: number; // for number input
  max?: number; // for number input
  rows?: number; // for textarea
  options?: Array<{ value: string | number; label: string }>; // for select
  className?: string;
}

export const FormField = React.forwardRef<
  HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement,
  FormFieldProps
>(
  (
    {
      label,
      name,
      type = 'text',
      value,
      onChange,
      onBlur,
      error,
      required = false,
      placeholder,
      helpText,
      disabled = false,
      maxLength,
      min,
      max,
      rows = 4,
      options = [],
      className,
    },
    ref
  ) => {
    const fieldId = `field-${name}`;
    const errorId = error ? `${fieldId}-error` : undefined;
    const helpId = helpText ? `${fieldId}-help` : undefined;

    const renderInput = () => {
      const commonProps = {
        id: fieldId,
        name,
        value,
        onChange,
        onBlur,
        disabled,
        required,
        placeholder,
        'aria-invalid': !!error,
        'aria-describedby':
          [errorId, helpId].filter(Boolean).join(' ') || undefined,
      };

      if (type === 'textarea') {
        return (
          <textarea
            {...commonProps}
            ref={ref as React.Ref<HTMLTextAreaElement>}
            rows={rows}
            maxLength={maxLength}
            className={`${styles.textarea} ${error ? styles.error : ''}`}
          />
        );
      }

      if (type === 'select') {
        return (
          <select
            {...commonProps}
            ref={ref as React.Ref<HTMLSelectElement>}
            className={`${styles.select} ${error ? styles.error : ''}`}
          >
            <option value="">Select {label}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      }

      return (
        <Input
          {...commonProps}
          ref={ref as React.Ref<HTMLInputElement>}
          type={type}
          error={!!error}
          maxLength={maxLength}
          min={min}
          max={max}
          fullWidth
        />
      );
    };

    return (
      <div className={`${styles.formField} ${className || ''}`}>
        <label htmlFor={fieldId} className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>

        {renderInput()}

        {helpText && !error && (
          <p id={helpId} className={styles.helpText}>
            {helpText}
          </p>
        )}

        {error && <ErrorMessage message={error} />}

        {maxLength && type === 'textarea' && (
          <div className={styles.charCount}>
            {String(value).length} / {maxLength}
          </div>
        )}
      </div>
    );
  }
);

FormField.displayName = 'FormField';
